/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { Info, Save, Plus, X, Shield } from 'lucide-react';
import { Button, CardGrid, SelectCardInput, Checkbox, Spinner } from '@crc-evans/ui-components';
import axiosConfig from '../../services/axiosConfig';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';

// Local Component: Info Banner
const InfoBanner = ({ title, description }) => (
  <div style={{
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    alignItems: 'flex-start'
  }}>
    <div style={{
      color: '#6366F1', // Indigo/Purple color for icon
      backgroundColor: '#EEF2FF',
      padding: '8px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Info size={24} />
    </div>
    <div>
      <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: '#0F172A', textAlign: 'left' }}>{title}</h3>
      <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>{description}</p>
    </div>
  </div>
);

// Local Component: CommentBox
const CommentBox = ({ header, value, onChange, placeholder }) => (
  <div style={{ marginTop: '40px' }}>
    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 500, color: '#64748B', textAlign: 'left' }}>
      {header}
    </h4>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={6}
      style={{
        width: '100%',
        padding: '16px',
        fontSize: '15px',
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        color: '#1F2937',
        backgroundColor: '#FFFFFF'
      }}
    />
  </div>
);

/** Convert display name to camelCase keyName (e.g. "safety error test" -> "safetyErrorTest") */
const toCamelCase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .trim()
    .split(/\s+|[_-]+/)
    .filter(Boolean)
    .map((word, i) => {
      const w = word.replace(/[^a-zA-Z0-9]/g, '');
      if (!w) return '';
      return i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join('');
};

// Default Data Helper
const getDefaultSafetyData = () => [
  { title: 'Restricted Workday Cases (RWC)', id: 'rwc', month: 0, total: 0, summary: 0 },
  { title: 'Medical Treatment Cases (MTC)', id: 'mtc', month: 0, total: 0, summary: 0 },
  { title: 'Near Misses', id: 'near-miss', month: 0, total: 0, summary: 0 },
  { title: 'First Aid Cases (FAC)', id: 'fac', month: 0, total: 0, summary: 0 },
  { title: 'Environmental Incidents', id: 'env', month: 0, total: 0, summary: 0 },
  { title: 'Other Incidents', id: 'other', month: 0, total: 0, summary: 0 },
  { title: 'Observation Cards Raised', id: 'obs-raised', month: 0, total: 0, summary: 0 },
  { title: 'Observation Cards Closed', id: 'obs-closed', month: 0, total: 0, summary: 0 },
  { title: 'Toolbox Talks', id: 'toolbox', month: 0, total: 0, summary: 0 },
];

const SafetyLog = ({
  safetyData: initialSafetyData = [],
  onSave: parentOnSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  date
}) => {
  // Initialize with initial data or defaults
  const [safetyData, setSafetyData] = useState(() => {
    return initialSafetyData.length > 0 ? initialSafetyData : getDefaultSafetyData();
  });
  const [remarks, setRemarks] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [reportNo, setReportNo] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const dispatch = useDispatch();
  const skipSafety = useSelector(state => {
    if (!sowId) return false;
    return state.dprReducer?.skipStatesBySection?.[sowId]?.skipSafety || false;
  });

  // Modal and Error state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newSafetyMetric, setNewSafetyMetric] = useState({ title: '', value: 0 });
  const [metricError, setMetricError] = useState('');

  const handleAddSafety = () => {
    setNewSafetyMetric({ title: '', value: 0 });
    setMetricError('');
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setNewSafetyMetric({ title: '', value: 0 });
  };

  const handleConfirmAddSafety = () => {
    if (!newSafetyMetric.title) return;

    // Case-insensitive duplicate check
    const isDuplicate = safetyData.some(item =>
      String(item.title).toLowerCase() === String(newSafetyMetric.title).toLowerCase()
    );

    if (isDuplicate) {
      const errorMsg = `Safety metric "${newSafetyMetric.title}" already exists.`;
      message.warning(errorMsg);
      setMetricError(errorMsg);
      return;
    }

    setMetricError('');

    const newMetric = {
      id: `safety-custom-${Date.now()}`,
      title: newSafetyMetric.title,
      month: 0,
      total: 0,
      summary: parseInt(newSafetyMetric.value, 10) || 0,
      today: parseInt(newSafetyMetric.value, 10) || 0,
      isCustom: true // Flag to identify custom added metrics if needed
    };

    setSafetyData(prev => [...prev, newMetric]);
    handleCloseModal();
  };

  // Fetch safety data from API when shift changes
  const fetchSafetyData = useCallback(async () => {
    // Even if no dprLogId, we might want to reset to defaults if context changes (like shift/date) 
    // but for now, if no ID, we keep existing local state which is defaults.
    if (!dprLogId) return;

    setIsLoading(true);
    try {
      const response = await axiosConfig.get(`/dpr-safety?dprLogId=${dprLogId}&shift=${shift}`);

      const { data } = response.data || {};
      const safetyMetricsData = data?.safetyMetrics;
      const notesData = data?.notes;
      const reportNoData = data?.reportNo;

      if (reportNoData) setReportNo(reportNoData);

      if (safetyMetricsData && Array.isArray(safetyMetricsData) && safetyMetricsData.length > 0) {
        const mappedSafetyData = safetyMetricsData.map((metric, index) => ({
          id: `safety-${index}-${metric.id || metric.keyName || Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          title: metric.name || metric.title || '',
          month: metric.toMonth ?? metric.month ?? 0,
          total: metric.toDateValue ?? metric.toDate ?? metric.total ?? 0,
          summary: metric.todayValue ?? metric.today ?? 0,
          originalMetric: metric
        }));
        setSafetyData(mappedSafetyData);
      } else {
        // If API returns successfully but no metrics, revert to defaults
        setSafetyData(getDefaultSafetyData());
      }

      setRemarks(data?.notes || data?.comments || data?.remarks || '');
    } catch (error) {
      console.error('Error fetching safety data:', error);
      // On error, we might want to keep current state or show error. 
      // For now, defaulting is safer for UI continuity.
      setSafetyData(getDefaultSafetyData());
    } finally {
      setIsLoading(false);
    }
  }, [shift, dprLogId]);

  useEffect(() => {
    if (dprLogId) {
      fetchSafetyData();
    } else {
      // If no ID, ensure we are in default state (clean form)
      setSafetyData(getDefaultSafetyData());
      setRemarks('');
    }
  }, [fetchSafetyData, dprLogId]);

  const handleSummaryChange = (itemId, value) => {
    if (!itemId) return;
    const numValue = (value === '' || isNaN(parseInt(value, 10))) ? '' : parseInt(value, 10);
    setSafetyData(prevData => prevData.map(item =>
      String(item.id) === String(itemId) ? { ...item, summary: numValue } : item
    ));
  };

  const handleSave = async () => {
    if (!dprLogId) {
      message.warning('Cannot save: DPR log not loaded.');
      return;
    }
    setIsSaving(true);
    try {
      const safetyMetrics = safetyData.map((item) => {
        const keyFromTitle = toCamelCase(item.title || '');
        const keyName = keyFromTitle || item.originalMetric?.keyName || item.id;
        return {
          keyName: typeof keyName === 'string' ? keyName : String(keyName),
          name: item.title || '',
          todayValue: item.summary === '' || item.summary === undefined ? 0 : Number(item.summary)
        };
      });

      const payload = {
        shift,
        dprSafety: {
          dprLogId,
          reportNo: reportNo || `DPR-${dprLogId}-S`,
          comments: remarks || '',
          notes: remarks || '',
          safetyMetrics
        }
      };

      await axiosConfig.put('/dpr-safety', payload);
      await fetchSafetyData();

      if (parentOnSave) {
        parentOnSave({ safetyData, remarks });
      }
      message.success('Safety report saved successfully.');
    } catch (error) {
      console.error('Error saving safety report:', error);
      const errMsg = error?.response?.data?.message || error?.message || 'Failed to save safety report.';
      message.error(errMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Spinner variant="inline" size="medium" />
        </div>
      )}
      <div>
        {/* White container for all content */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          {/* Simple header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <div style={{
                color: '#6366F1',
                backgroundColor: '#EEF2FF',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Info size={24} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 700, color: '#0F172A', textAlign: 'left' }}>Safety Log</h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748B', textAlign: 'left' }}>Track daily safety incidents and observations.</p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Checkbox
                label="Skip Safety"
                checked={skipSafety}
                onChange={(checked) => sowId && dispatch(updateDprSkipState(sowId, 'skipSafety', checked))}
                borderColor="#00C9A7"
              />
              <Button
                label="Add Safety"
                variant="primary"
                size="small"
                icon={Plus}
                onClick={handleAddSafety}
              />
            </div>
          </div>

          <CardGrid columns={2}>
            {safetyData.map((item) => (
              <SelectCardInput
                key={item.id}
                highlightBorderColor="#00C9A7"
                id={item.id}
                inputValue={item.summary ?? ''}
                onClick={() => { }}
                onInputChange={(val) => handleSummaryChange(item.id, val)}
                pills={[
                  { label: 'MONTH', value: item.month ?? 0 },
                  { label: 'TOTAL', value: item.total ?? 0 }
                ]}
                title={item.title || ''}
              />
            ))}
          </CardGrid>

          <CommentBox
            header="Safety Observations / Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Enter any additional details regarding safety incidents, near misses, or toolbox talks..."
          />

          {/* Save button sticky at bottom */}
          <div style={{
            position: 'sticky',
            bottom: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '24px',
            paddingTop: '16px',
            zIndex: 10
          }}>
            <Button
              label="Save Safety Report"
              variant="secondary"
              size="large"
              icon={Save}
              onClick={handleSave}
              disabled={isSaving || !dprLogId || isSubmitting}
            />
          </div>
        </div>
      </div>

      {isModalVisible && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '100px',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            width: '500px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{
                  backgroundColor: '#EEF2FF', // Light Indigo
                  padding: '10px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4F46E5' // Indigo 600
                }}>
                  <Shield size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                    Add Safety Metric
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
                    Add to safety log details
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF',
                  padding: '4px'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Safety Header Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Near Misses"
                  value={newSafetyMetric.title}
                  onChange={(e) => setNewSafetyMetric({ ...newSafetyMetric, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                />
              </div>
              {metricError && (
                <div style={{
                  color: '#EF4444',
                  fontSize: '13px',
                  textAlign: 'left',
                  marginTop: '-12px',
                  paddingLeft: '4px'
                }}>
                  {metricError}
                </div>
              )}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  textAlign: 'left'
                }}>
                  Value
                </label>
                <input
                  type="number"
                  placeholder="0"
                  value={newSafetyMetric.value}
                  onChange={(e) => setNewSafetyMetric({ ...newSafetyMetric, value: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px',
                    outline: 'none',
                    color: '#1F2937',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                  onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                />
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#F9FAFB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #E5E7EB'
            }}>
              <Button
                label="Cancel"
                variant="tertiary"
                onClick={handleCloseModal}
                size="medium"
              />
              <Button
                label="Add Metric"
                variant="primary"
                onClick={handleConfirmAddSafety}
                disabled={!newSafetyMetric.title}
                size="medium"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyLog;
